import React, { useState, useEffect } from 'react'
import { t } from 'i18next'
import { Divider } from 'components/divider'
import { ArrowLeftIcon } from '@heroicons/react/20/solid'
import { useNavigate, useParams } from 'react-router-dom'
import { FileAttachment } from 'components/custom/FileAttachment'
import { useLazyQuery, useMutation } from '@apollo/client'
import { GET_VESSEL_CHECK_SUBMISSION_DETAILS } from 'graphql/operations/queries'
import loadingImage from '../../../../assets/images/boat-loading.gif'
import { customDateTimeFormat } from 'utils/common'
import { ErrorDialog, SuccessDialog } from 'components/custom/ResponseDialog'
import { MARK_COMPLETE_VESSEL_DEFECT } from 'graphql/operations/mutations'
import { ImageDisplay } from 'components/custom/ImageDisplay'
import { Badge } from 'components/badge'
import { GraphQlResponseNoData } from 'components/custom/GraphQlResponseDisplay'

export default function DefectListDetails() {
    const { id, submission_date } = useParams()
    console.log('Submission ID: ', id)
    // Dialog Message
    const [showErrDialog, setShowErrDialog] = useState(false)
    const [errorMessage, setErrorMessage] = useState('')
    const [openAccordions, setOpenAccordions] = useState([true, true, true]) // All accordions are open by default
    const [defectLists, setDefectLists] = useState(null)
    const [inspectionReportRemarks, setInspectionReportRemarks] = useState(null)
    const [inspectionReportAttachments, setInspectionReportAttachments] = useState([])
    const [isMandatoryError, setIsMandatoryError] = useState(false)
    const [showSucDialog, setShowSucDialog] = useState(false)
    const [submitLoading, setSubmitLoading] = useState(false)
    const [defectStatus, setDefectStatus] = useState(true)

    const toggleAccordion = index => {
        setOpenAccordions(prevState => prevState.map((isOpen, i) => (i === index ? !isOpen : isOpen)))
    }
    const navigate = useNavigate()
    const handleBack = () => {
        navigate('/vessel/defect-list')
    }
    const [getDefectList, { loading: defectListLoading, error: defectListError }] = useLazyQuery(
        GET_VESSEL_CHECK_SUBMISSION_DETAILS,
        {
            fetchPolicy: 'network-only',
            notifyOnNetworkStatusChange: true,
            onCompleted: data => {
                if (data.getVesselDefectSubmission.success && data.getVesselDefectSubmission.success) {
                    const defect = data.getVesselDefectSubmission.defects[0]
                    
                    setDefectLists(defect)
                    setInspectionReportAttachments(defect.inspection?.attachments || [])
                    setInspectionReportRemarks(defect.inspection?.remarks || null)
                    setDefectStatus(defect.defect_status.code)
                }
            },
        }
    )
    useEffect(() => {
        setTimeout(() => {
            if (id) {
                getDefectList({
                    variables: {
                        page: {
                            limit: 1,
                            offset: 0,
                        },
                        toDate: submission_date.split('T')[0],
                        fromDate: submission_date.split('T')[0],
                        defectSubmissionId: id,
                    },
                })
            }
        }, 50)
    }, [id, submission_date])

    const handleAttachmentsChange = attachments => {
        const newAttachments = attachments.map(attachment => {
            const { file_data, ...rest } = attachment
            return rest
        })
        
        setInspectionReportAttachments(newAttachments)
        setIsMandatoryError(false)
    }
    // Mutation Update Boat
    const [markCompleteVesselDefect, { loading: updateLoading }] = useMutation(MARK_COMPLETE_VESSEL_DEFECT, {
        onCompleted: data => {
            if (data?.markCompleteVesselDefect.code === '200' && data?.markCompleteVesselDefect.success === true) {
                setSubmitLoading(false)
                setShowSucDialog(true)
                setTimeout(() => {
                    handleBack()
                }, 500)
            } else {
                setSubmitLoading(false)
                setShowErrDialog(true)
                setErrorMessage(data?.markCompleteVesselDefect.message)
            }
        },
        onError: error => {
            setSubmitLoading(false)
            setShowErrDialog(true)
            setErrorMessage(error.message)
        },
    })

    const handleDialogClose = () => {
        setShowErrDialog(false)
        setShowSucDialog(false)
    }

    const handleSubmit = () => {
        if (inspectionReportRemarks === null || inspectionReportAttachments.length === 0) {
            setIsMandatoryError(true)
        } else {
            setIsMandatoryError(false)
            setSubmitLoading(true)
            markCompleteVesselDefect({
                variables: {
                    inspectionReport: {
                        remarks: inspectionReportRemarks,
                        attachments: inspectionReportAttachments,
                    },
                    defectDate: defectLists.defect_date.split('T')[0],
                    defectSubmissionId: defectLists.defect_submission_id,
                },
            })
        }
        console.log('Remarks: ', inspectionReportRemarks)
        console.log('Attachments: ', inspectionReportAttachments)
    }
    return (
        <>
            <div className="mb-6 flex justify-normal items-center">
                <button
                    onClick={handleBack}
                    type="button"
                    className="inline-flex items-center gap-x-0.5 rounded-md text-sm text-gray-600"
                >
                    <ArrowLeftIcon aria-hidden="true" className="-ml-0.5 h-5 w-4 rtl:rotate-180" />
                    {t('Back')}
                </button>
                <h3 className="text-lg font-bold leading-7 text-primary ps-2.5 pb-1">{t('Defect details')}</h3>
            </div>
            <div className="flex flex-wrap items-end justify-between pb-0">
                <div className="max-sm:w-full sm:flex-1">
                    <div className="mt-4">
                        <div className="px-4 sm:px-0">
                            <h2 className="text-base font-semibold leading-7 text-primary">{t('Boat Information')}</h2>
                            <p className="mt-1 text-sm leading-6 text-gray-600">
                                {t('Information about the boat and the captain who submitted this checklist.')}
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {defectListLoading && (
                <div className="flex flex-col justify-center items-center bg-white h-96 mt-16">
                    <img alt="logo" src={loadingImage} className="w-[250px]" />
                    <p className="pt-3 text-primary font-semibold">{t('Loading, please wait')}...</p>
                </div>
            )}
            {defectListError && (
                <div className="text-center py-24 bg-gray-50 mt-8 rounded-lg">
                    <svg
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        aria-hidden="true"
                        className="mx-auto h-12 w-12 text-gray-400"
                    >
                        <path
                            d="M16.6725 16.6412L21 21M19 11C19 15.4183 15.4183 19 11 19C6.58172 19 3 15.4183 3 11C3 6.58172 6.58172 3 11 3C15.4183 3 19 6.58172 19 11Z"
                            strokeWidth={2}
                            vectorEffect="non-scaling-stroke"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                    <h3 className="mt-2 text-sm font-semibold text-gray-900">{t('Failed to fetch records')}.</h3>
                    <p className="mt-1 text-sm text-gray-500">
                        {t('Please contact the administrator or try again later.')}
                    </p>
                    <div className="mt-6"></div>
                </div>
            )}
            {!defectListLoading && !defectListError && defectLists == null && <GraphQlResponseNoData />}
            {!defectListLoading && !defectListError && defectLists != null && (
                <>
                    <div className="flex flex-wrap items-end justify-between gap-4 pb-9">
                        <div className="max-sm:w-full sm:flex-1">
                            <div className="mt-4">
                                <div className="mt-3 border-t border-gray-100">
                                    <dl className="divide-y divide-gray-100">
                                        <div className="bg-white px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-3">
                                            <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                                                {t('Boat Name')}
                                            </dd>
                                            <dt className="text-sm font-medium leading-6 text-gray-900">
                                                {defectLists.vessel?.boat?.name?.en}
                                            </dt>
                                        </div>
                                        <div className="bg-gray-50 px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-3">
                                            <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                                                {t('Boat Number')}
                                            </dd>
                                            <dt className="text-sm font-medium leading-6 text-gray-900">
                                                {defectLists.vessel?.boat?.details?.reg_number}
                                            </dt>
                                        </div>
                                    </dl>
                                </div>
                            </div>
                        </div>
                        <div className="max-sm:w-full sm:flex-1">
                            <div className="mt-4">
                                <div className="mt-3 border-t border-gray-100">
                                    <dl className="divide-y divide-gray-100">
                                        <div className="bg-white px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-3">
                                            <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                                                {t('Submitted On')}
                                            </dd>
                                            <dt className="text-sm font-medium leading-6 text-gray-900">
                                                {customDateTimeFormat(defectLists.defect_date)}
                                            </dt>
                                        </div>
                                        <div className="bg-gray-50 px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-3">
                                            <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                                                {t('Submitted By')}
                                            </dd>
                                            <dt className="text-sm font-medium leading-6 text-gray-900">
                                                {defectLists.captain.user_info.given_name}{' '}
                                                {defectLists.captain.user_info.family_name}
                                            </dt>
                                        </div>
                                    </dl>
                                </div>
                            </div>
                        </div>
                    </div>
                    <Divider />
                    <div className="bg-white px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-3">
                        <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">{t('Status')}</dd>
                        <dt className="text-sm font-medium leading-6 text-gray-900 text-end">
                            <Badge
                                color={
                                    defectLists.defect_status.code === 'CMP'
                                        ? 'zinc'
                                        : defectLists.defect_status.code === 'CRE'
                                        ? 'emerald'
                                        : 'rose'
                                }
                            >
                                {defectLists.defect_status.description.en}
                            </Badge>
                        </dt>
                    </div>

                    <Divider />
                    <div className="space-y-10 divide-y divide-gray-900/10 mt-8 mb-4">
                        <div className="grid grid-cols-1 gap-x-8 gap-y-8 md:grid-cols-3 pb-0">
                            <div className="px-4 sm:px-0">
                                <h2 className="text-base font-semibold leading-7 text-primary">
                                    {t('Defect list information')}
                                </h2>
                                <p className="mt-1 text-sm leading-6 text-gray-600">
                                    {t('Information about the defects reported by the captain.')}
                                </p>
                            </div>

                            <div className="col-span-2">
                                <div>
                                    <div className="pb-0">
                                        {defectLists.defects.map((defect, index) => (
                                            <div
                                                key={defect.defect_checklist_code}
                                                className="w-full border border-gray-200 rounded shadow-sm mb-4"
                                            >
                                                <div
                                                    onClick={() => toggleAccordion(index)}
                                                    className="flex text-md justify-between items-center bg-blue-500 text-primary px-4 py-2 font-semibold cursor-pointer"
                                                >
                                                    <span>{defect.defect_checklist.description.en}</span>
                                                    <span>
                                                        {openAccordions[index] ? (
                                                            <svg
                                                                className="w-4 h-4"
                                                                fill="none"
                                                                stroke="currentColor"
                                                                viewBox="0 0 24 24"
                                                                xmlns="http://www.w3.org/2000/svg"
                                                            >
                                                                <path
                                                                    strokeLinecap="round"
                                                                    strokeLinejoin="round"
                                                                    strokeWidth="2"
                                                                    d="M20 12H4"
                                                                ></path>
                                                            </svg>
                                                        ) : (
                                                            <svg
                                                                className="w-4 h-4"
                                                                fill="none"
                                                                stroke="currentColor"
                                                                viewBox="0 0 24 24"
                                                                xmlns="http://www.w3.org/2000/svg"
                                                            >
                                                                <path
                                                                    strokeLinecap="round"
                                                                    strokeLinejoin="round"
                                                                    strokeWidth="2"
                                                                    d="M12 4v16m8-8H4"
                                                                ></path>
                                                            </svg>
                                                        )}
                                                    </span>
                                                </div>
                                                {openAccordions[index] && (
                                                    <div className="bg-white px-4 py-3">
                                                        <div className="w-full p-2 pt-0">
                                                            {defect.remarks && (
                                                                <div className="border-t border-gray-100 px-4 py-2 sm:col-span-2 sm:px-0">
                                                                    <dt className="text-sm font-medium leading-6 text-gray-900">
                                                                        {t('Remarks')}
                                                                    </dt>
                                                                    <dd className="mt-0 text-sm leading-6 text-gray-700 ">
                                                                        {defect.remarks || '-'}
                                                                    </dd>
                                                                </div>
                                                            )}

                                                            <div className="border-t border-gray-100 px-4 py-4 sm:col-span-2 sm:px-0">
                                                                <dt className="text-sm font-medium leading-6 text-gray-900">
                                                                    {t('Attachments')}
                                                                </dt>
                                                                <ul className="grid grid-cols-2 gap-x-4 gap-y-8 sm:grid-cols-3 sm:gap-x-6 lg:grid-cols-4 xl:gap-x-8">
                                                                    {defect.attachments.map((file, index) => (
                                                                        <li
                                                                            key={file.file_name}
                                                                            className="relative mt-3"
                                                                        >
                                                                            <div className="group h-[150px] aspect-w-5 block w-full overflow-hidden rounded-lg bg-gray-100 focus-within:ring-2 focus-within:ring-indigo-500 focus-within:ring-offset-2 focus-within:ring-offset-gray-100">
                                                                                <ImageDisplay
                                                                                    s3Key={file.s3_key}
                                                                                    file={file.file_data}
                                                                                    classStyle="object-cover group-hover:opacity-75 w-full h-full"
                                                                                />
                                                                                <button
                                                                                    type="button"
                                                                                    className="absolute inset-0 focus:outline-none"
                                                                                >
                                                                                    {/* <span className="sr-only">
                                                                                        {t('View details for')}{' '}
                                                                                        {file.file_name}
                                                                                    </span> */}
                                                                                </button>
                                                                            </div>
                                                                            {/* <p className="pointer-events-none mt-2 block truncate text-sm font-medium text-gray-900">
                                                                                {file.file_name}
                                                                            </p> */}
                                                                            {/* <p className="pointer-events-none block text-sm font-medium text-gray-500">
                                                                                {file.size}
                                                                            </p> */}
                                                                        </li>
                                                                    ))}
                                                                </ul>
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                                {/* <div className="flex items-center justify-end gap-x-6 border-t border-gray-900/10 px-4 py-4 sm:px-8">
                            <button type="button" className="text-sm font-semibold leading-6 text-gray-900">
                                Cancel
                            </button>
                            <button
                                type="submit"
                                className="rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                            >
                                Save
                            </button>
                        </div> */}
                            </div>
                        </div>
                    </div>
                    <Divider />
                    <div className="space-y-10 divide-y divide-gray-900/10 mt-6">
                        <div className="grid grid-cols-1 gap-x-8 gap-y-8 md:grid-cols-3 pb-6">
                            <div className="px-4 sm:px-0">
                                <h2 className="text-base font-semibold leading-7 text-primary">
                                    {t('Inspection Report')}
                                </h2>
                                <p className="mt-1 text-sm leading-6 text-gray-600">
                                    {t(
                                        'Admins can add/View remarks and attachments to captain-submitted defect reports.'
                                    )}
                                </p>
                            </div>

                            <div className="col-span-2">
                                <div>
                                    <div className="grid max-w-full grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-6 md:col-span-2">
                                        <div className="sm:col-span-full">
                                            <label
                                                htmlFor="description"
                                                className="block text-sm font-medium leading-6 text-gray-900"
                                            >
                                                {t('Remarks')}
                                                <span className="text-red">*</span>
                                            </label>
                                            {defectStatus != 'CMP' && (
                                                <>
                                                    <textarea
                                                        id="description"
                                                        name="description"
                                                        value={inspectionReportRemarks}
                                                        rows="4"
                                                        onChange={e => {
                                                            const value = e.target.value.trim()
                                                            setInspectionReportRemarks(value || null)
                                                            setIsMandatoryError(false)
                                                        }}
                                                        placeholder="Enter here"
                                                        className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6"
                                                    />
                                                    {isMandatoryError && inspectionReportRemarks === null && (
                                                        <p className="text-error text-xs mt-1">
                                                            {t('Please enter remarks')}
                                                        </p>
                                                    )}
                                                </>
                                            )}
                                            {defectStatus == 'CMP' && (
                                                <div className="mt-1">
                                                    <p className="text-sm leading-5 text-gray-700">
                                                        {inspectionReportRemarks}
                                                    </p>
                                                </div>
                                            )}
                                        </div>
                                        {defectStatus != 'CMP' && (
                                            <FileAttachment
                                                isRequired={true}
                                                maxLength={5}
                                                uploadLocation="private/vessel/deflects"
                                                title={t('Attachments')}
                                                onChange={handleAttachmentsChange}
                                                attachments={inspectionReportAttachments}
                                                maxSize={5}
                                            ></FileAttachment>
                                        )}
                                        {defectStatus == 'CMP' && inspectionReportAttachments.length > 0 && (
                                            <div className="sm:col-span-full">
                                                <ul className="grid grid-cols-3 gap-x-2 gap-y-5 sm:grid-cols-2 sm:gap-x-6 lg:grid-cols-3 xl:gap-x-2">
                                                    {inspectionReportAttachments.map(file => (
                                                        <li
                                                            key={file.s3_key}
                                                            className="relative group w-auto overflow grid"
                                                        >
                                                            <div className="h-auto block w-auto overflow-hidden rounded-lg bg-gray-100 focus-within:ring-2 focus-within:ring-indigo-500 focus-within:ring-offset-2 focus-within:ring-offset-gray-100">
                                                                <ImageDisplay
                                                                    s3Key={file.s3_key}
                                                                    file={file.file_data}
                                                                    classStyle="object-cover group-hover:opacity-75 w-full h-full"
                                                                />
                                                            </div>
                                                            {/* <div className="w-auto flex justify-between items-center px-0 py-2">
                                                                <p className="pointer-events-none block truncate text-sm font-medium text-gray-900 group-hover:text-primary">
                                                                    {file.file_name}
                                                                </p>

                                                            </div> */}
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}
                                    </div>
                                    {isMandatoryError && inspectionReportAttachments.length === 0 && (
                                        <p className="text-error text-xs mt-1">
                                            {t('Please upload at least one attachment')}
                                        </p>
                                    )}
                                </div>
                                {defectStatus != 'CMP' && (
                                    <div className="mt-5 flex items-center justify-end gap-x-6 border-t border-gray-900/10 px-4 py-4 sm:px-8">
                                        <button
                                            onClick={handleBack}
                                            type="button"
                                            className="text-sm font-semibold leading-6 text-gray-900"
                                        >
                                            {t('Cancel')}
                                        </button>
                                        <button
                                            disabled={updateLoading}
                                            onClick={() => {
                                                handleSubmit()
                                            }}
                                            type="submit"
                                            className="hover:bg-primary/[.9] bg-primary focus-visible:outline-primary justify-center rounded-md px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
                                        >
                                            {t('Save')}
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                    <Divider />
                </>
            )}
            {showErrDialog && (
                <ErrorDialog status={true} title="Error" message={errorMessage} onClose={handleDialogClose} />
            )}
            {showSucDialog && (
                <SuccessDialog
                    status={true}
                    title={t('Success')}
                    message={t('Saved Successfully')}
                    onClose={handleDialogClose}
                />
            )}
            {/* Overlay and Loading Spinner */}
            {submitLoading && (
                <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
                    <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-white"></div>
                </div>
            )}
        </>
    )
}
